/**
 * useCanonicalNet — fetch the binding net-supported claim from /case-figures.
 *
 * Returns the canonical net (policy-deductions engine output) plus the
 * bottom-up and top-down cross-checks. On 404 or any error, net is null
 * and no fallback figure is substituted — the UI shows "—".
 */

import { useState, useEffect } from 'react';
import { getCaseFigures, type CaseFiguresResponse } from '../services/caseFiguresApi';

export interface UseCanonicalNetResult {
  net: number | null;
  bottomUp: number | null;
  topDown: number | null;
  computedAt: string | null;
  electedMethod: string | null;
  loading: boolean;
  error: string | null;
}

export function useCanonicalNet(reportId: string | undefined): UseCanonicalNetResult {
  const [result, setResult] = useState<UseCanonicalNetResult>({
    net: null,
    bottomUp: null,
    topDown: null,
    computedAt: null,
    electedMethod: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!reportId) {
      setResult({
        net: null,
        bottomUp: null,
        topDown: null,
        computedAt: null,
        electedMethod: null,
        loading: false,
        error: null,
      });
      return;
    }

    let cancelled = false;
    setResult({
      net: null,
      bottomUp: null,
      topDown: null,
      computedAt: null,
      electedMethod: null,
      loading: true,
      error: null,
    });

    getCaseFigures(reportId)
      .then((data: CaseFiguresResponse) => {
        if (cancelled) return;
        const figures = data.figures ?? {};
        setResult({
          net: figures.net_supported_claim?.value ?? null,
          bottomUp: figures.bi_loss_bottom_up?.value ?? null,
          topDown: figures.bi_loss_top_down?.value ?? null,
          computedAt: figures.net_supported_claim?.computed_at ?? null,
          electedMethod: data.elected_method ?? null,
          loading: false,
          error: null,
        });
      })
      .catch((err: any) => {
        if (cancelled) return;
        setResult({
          net: null,
          bottomUp: null,
          topDown: null,
          computedAt: null,
          electedMethod: null,
          loading: false,
          error: err?.message || 'Failed to load case figures',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [reportId]);

  return result;
}
